import { Button } from '@sunwebgroup/uds-ui/button'

import { countryData } from '@sunwebgroup/uds-ui/fields/phone-field'

import { cn } from '@sunwebgroup/uds-ui/utils'
import * as React from 'react'

import { FieldErrors, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useSetParticipantGroupForm } from '@/domain/groups/hooks'
import { PersonalDataFormInput } from '@/domain/groups/types'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import formInputStyles from '@/scopes/checkout/components/PersonalDetailsForm/Inputs/FormInput.module.css'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { shouldShowHelpTextForMobilePhoneInputOnPersonalDetailsStep } from '@/scopes/checkout/domain/policies/siteName'
import { ParticipantTitle } from '@/scopes/checkout/domain/types/participantTitle'
import { DateInput } from '@/scopes/common/components/FormElements/DateInput'
import { PhoneInput } from '@/scopes/common/components/FormElements/PhoneInput'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import { ToggleButtonGroupInput } from '@/scopes/common/components/FormElements/ToggleButtonGroupInput'
import {
  getPhoneDigitsFromPattern,
  getPhoneExampleFromDigits,
} from '@/scopes/common/components/FormElements/utils/phonePatternUtils'
import { scrollToFirstError } from '@/scopes/common/components/FormElements/utils/scrollToFirstError'
import { getDefaultPhoneCountry } from '@/scopes/common/domain/policies/defaultPhoneCountry'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import styles from './PersonalDetailsForm.module.css'

export function PersonalDetailsForm() {
  const MIN_AGE = 15
  const bookingDraftQueryResult = useBookingDraftQuery()
  const { setParticipantsForm } = useSetParticipantGroupForm(bookingDraftQueryResult)
  const data = bookingDraftQueryResult.data
  const participants = data?.participants ?? {}
  const mainParticipant = Object.values(participants)[0]
  const locale = useConfiguration('locale')
  const defaultCountry = getDefaultPhoneCountry(locale)
  const departureDate = data?.analytics?.departureDate

  const methods = useForm<PersonalDataFormInput>({
    mode: 'onBlur',
    defaultValues: {
      title: normalizeParticipantTitle(mainParticipant?.title) as PersonalDataFormInput['title'],
      firstName: mainParticipant?.name ?? '',
      lastName: mainParticipant?.surname ?? '',
      birthDate: mainParticipant?.birthdate ? new Date(mainParticipant?.birthdate) : undefined,
      mobilePhoneNumber: mainParticipant?.mobilePhoneNumber ?? { phoneNumber: '', countryCode: defaultCountry.code },
      emergencyPhoneNumber: mainParticipant?.emergencyPhoneNumber ?? {
        phoneNumber: '',
        countryCode: defaultCountry.code,
      },
      email: mainParticipant?.email ?? '',
    },
  })

  const {
    handleSubmit,
    formState: { isSubmitSuccessful },
    reset,
  } = methods

  React.useEffect(() => {
    if (!mainParticipant)
      return

    reset({
      title: normalizeParticipantTitle(mainParticipant.title) as PersonalDataFormInput['title'],
      firstName: mainParticipant.name ?? '',
      lastName: mainParticipant.surname ?? '',
      birthDate: mainParticipant?.birthdate ? new Date(mainParticipant?.birthdate) : undefined,
      mobilePhoneNumber: mainParticipant?.mobilePhoneNumber ?? { phoneNumber: '', countryCode: defaultCountry.code },
      emergencyPhoneNumber: mainParticipant?.emergencyPhoneNumber ?? {
        phoneNumber: '',
        countryCode: defaultCountry.code,
      },
      email: mainParticipant.email ?? '',
    })
  }, [defaultCountry.code, mainParticipant, reset])

  const dateFormat = useConfiguration('dateFormat')

  const intl = useIntl()

  const nameValidationPattern = '^[A-Za-zÆæØøÅåÄäÖöÜüßÉéÀàÈèÙùÂâÊêÎîÔôÛûËëÏïŸÿÇçÁáÍíÓóÚú\'\\-]{2,}( [A-Za-zÆæØøÅåÄäÖöÜüßÉéÀàÈèÙùÂâÊêÎîÔôÛûËëÏïŸÿÇçÁáÍíÓóÚú\'\\-]{2,})*$'
  const emailValidationPattern
    = '^[a-zA-Z0-9!#$%&\'*+\\/=?^_`\\{\\|\\}~\\-]+(?:\\.[a-zA-Z0-9!#$%&\'*+\\/=?^_`\\{\\|\\}~\\-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9\\-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9\\-]*[a-zA-Z0-9])?$'

  const onSubmit: SubmitHandler<PersonalDataFormInput> = (result: PersonalDataFormInput) => {
    const birthDate
      = result.birthDate instanceof Date ? result.birthDate.toISOString().split('T')[0] : result.birthDate
    setParticipantsForm({ ...result, birthDate })
  }

  const onInvalid = (errors: FieldErrors<PersonalDataFormInput>) => {
    scrollToFirstError(errors)
  }

  const siteName = useConfiguration('siteName')
  const titleOptions = [
    { value: ParticipantTitle.Mr, title: intl.formatMessage({ id: 'checkout_steps_participants_form_field_title_mr_text' }) },
    { value: ParticipantTitle.Ms, title: intl.formatMessage({ id: 'checkout_steps_participants_form_field_title_ms_text' }) },
  ]

  const parsedDepartureDate = departureDate ? new Date(departureDate) : undefined
  const referenceDate
    = parsedDepartureDate && !Number.isNaN(parsedDepartureDate.getTime()) ? parsedDepartureDate : new Date()

  const maxBirthDate = new Date(referenceDate)
  maxBirthDate.setFullYear(maxBirthDate.getFullYear() - MIN_AGE)

  const minBirthDate = new Date(referenceDate)
  minBirthDate.setFullYear(minBirthDate.getFullYear() - 100)

  const dateRange = {
    min: minBirthDate.toISOString().split('T')[0],
    max: maxBirthDate.toISOString().split('T')[0],
  }

  const mobilePhoneInputHelpText = shouldShowHelpTextForMobilePhoneInputOnPersonalDetailsStep(siteName)
    ? intl.formatMessage({
        id: 'checkout_steps_participants_form_field_help_text_mobile_phone',
      })
    : undefined

  return (
    <FormProvider {...methods}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <CheckoutSection title="">
          <div className={styles.fields}>
            <ToggleButtonGroupInput
              name="title"
              label={intl.formatMessage({ id: 'checkout_steps_participants_form_field_label_title' })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_required_error_message_title_main_booker',
              })}
              items={titleOptions}
              formFieldClass={cn(styles.personalDetailsFormField, styles.alignmentRight, styles.sizeFull)}
            />
            <TextInput
              name="firstName"
              label={intl.formatMessage({ id: 'checkout_steps_participants_form_field_label_name' })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_required_error_message_name',
              })}
              pattern={nameValidationPattern}
              patternErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_pattern_error_message_name',
              })}
              formFieldClass={cn(styles.personalDetailsFormField, styles.alignmentRight, styles.sizeFull)}
              helpText={intl.formatMessageIfExists({ id: 'checkout_steps_participants_form_field_help_text_name' })}
            />
            <TextInput
              name="lastName"
              label={intl.formatMessage({ id: 'checkout_steps_participants_form_field_label_surname' })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_required_error_message_surname',
              })}
              pattern={nameValidationPattern}
              patternErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_pattern_error_message_surname',
              })}
              formFieldClass={cn(styles.personalDetailsFormField, styles.alignmentRight, styles.sizeFull)}
              helpText={intl.formatMessageIfExists({ id: 'checkout_steps_participants_form_field_help_text_surname' })}
            />
            <DateInput
              name="birthDate"
              label={intl.formatMessage({ id: 'checkout_steps_participants_form_field_label_birthdate' })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_required_error_message_birthdate',
              })}
              patternErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_pattern_error_message_birthdate',
              })}
              validateErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_validate_error_message_generic_birthdate',
              })}
              formFieldClass={cn(styles.personalDetailsFormField, styles.alignmentRight, formInputStyles.formFieldS)}
              dateRange={dateRange}
              dateFormat={dateFormat}
            />
            <PhoneInput
              key={`mobile-${mainParticipant?.mobilePhoneNumber?.countryCode ?? defaultCountry.code}`}
              name="mobilePhoneNumber"
              label={intl.formatMessage({ id: 'checkout_steps_participants_form_field_label_mobile_phone' })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_required_error_message_mobile_phone',
              })}
              patternErrorMessage={(selectedCountry) => {
                const phoneDigits = getPhoneDigitsFromPattern(selectedCountry?.pattern)

                return intl.formatMessage(
                  { id: 'checkout_steps_participants_form_field_pattern_error_message_mobile_phone' },
                  { phoneDigits, phoneExample: getPhoneExampleFromDigits(phoneDigits) },
                )
              }}
              countries={countryData}
              formFieldClass={cn(styles.personalDetailsFormField, styles.alignmentRight, formInputStyles.formFieldM)}
              helpText={mobilePhoneInputHelpText}
            />

            <PhoneInput
              key={`emergency-${mainParticipant?.emergencyPhoneNumber?.countryCode ?? defaultCountry.code}`}
              name="emergencyPhoneNumber"
              label={intl.formatMessage({ id: 'checkout_steps_participants_form_field_label_emergency_phone' })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_required_error_message_emergency_phone',
              })}
              patternErrorMessage={(selectedCountry) => {
                const phoneDigits = getPhoneDigitsFromPattern(selectedCountry?.pattern)

                return intl.formatMessage(
                  { id: 'checkout_steps_participants_form_field_pattern_error_message_emergency_phone' },
                  { phoneDigits, phoneExample: getPhoneExampleFromDigits(phoneDigits) },
                )
              }}
              countries={countryData}
              formFieldClass={cn(styles.personalDetailsFormField, styles.alignmentRight, formInputStyles.formFieldM)}
            />

            <TextInput
              name="email"
              label={intl.formatMessage({ id: 'checkout_steps_participants_form_field_label_email' })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_required_error_message_email',
              })}
              pattern={emailValidationPattern}
              patternErrorMessage={intl.formatMessage({
                id: 'checkout_steps_participants_form_field_pattern_error_message_email',
              })}
              formFieldClass={cn(styles.personalDetailsFormField, styles.alignmentRight, styles.sizeFull)}
            />
          </div>
        </CheckoutSection>
        <Button type="submit" disabled={isSubmitSuccessful}>
          {intl.formatMessage({ id: 'groups_personal_data_step_next_button' })}
        </Button>
      </form>
    </FormProvider>
  )
}

function normalizeParticipantTitle(title?: string) {
  const normalizedTitle = title?.toLowerCase()

  if (normalizedTitle === 'mr') {
    return ParticipantTitle.Mr
  }

  if (normalizedTitle === 'ms') {
    return ParticipantTitle.Ms
  }

  return undefined
}
